/**
 * Descuento por producto (por línea) en el POS.
 *
 * Se activa por organización desde Empresa → Datos → Punto de venta:
 * - organizations.enable_item_discount            (lógico principal)
 * - organizations.item_discount_max_pct           (tope; null = sin tope)
 * - organizations.item_discount_reason_from_pct   (motivo obligatorio desde; null = opcional)
 *
 * Convención del carrito: `unit_price` y `subtotal` quedan YA descontados y el
 * precio original vive en `list_unit_price`. Así todo lo que hoy lee
 * unit_price/subtotal (total, descuento global, factura ARCA, rentabilidad,
 * devoluciones) toma el neto de la línea sin cambios. El descuento global de la
 * venta se aplica después, sobre ese neto.
 *
 * Spec: desarrollo/spec-descuento-por-item-pos.md (proyecto Impulza).
 */

export const ITEM_DISCOUNT_AUTORIZAR_MODULE_ID = 'ventas-pos-descuento-autorizar';

export const MOTIVOS_DESCUENTO_ITEM = [
  'Cliente frecuente',
  'Producto dañado',
  'Vencimiento próximo',
  'Igualar precio',
  'Otro',
];

const PRIVILEGED_ROLES = new Set(['admin', 'administrador']);

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;
const round4 = (value) => Math.round((Number(value) || 0) * 10000) / 10000;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const pctOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? clamp(n, 0, 100) : null;
};

export function normalizarConfigDescuentoItem(org) {
  return {
    enabled: Boolean(org?.enable_item_discount),
    maxPct: pctOrNull(org?.item_discount_max_pct),
    motivoDesdePct: pctOrNull(org?.item_discount_reason_from_pct),
  };
}

/** Precio de lista de la línea (el original, antes del descuento propio). */
export const precioListaItem = (item) => {
  const lista = item?.list_unit_price;
  return Number(lista ?? item?.unit_price) || 0;
};

export const tieneDescuentoItem = (item) => Number(item?.item_discount_pct) > 0;

/**
 * Quita el descuento de línea: vuelve al precio de lista.
 */
export function quitarDescuentoItem(item) {
  if (!item) return item;
  const lista = precioListaItem(item);
  const cantidad = Number(item.cantidad) || 0;
  const {
    list_unit_price: _lista,
    item_discount_pct: _pct,
    item_discount_by: _by,
    item_discount_reason: _reason,
    ...resto
  } = item;
  return { ...resto, unit_price: lista, subtotal: round2(lista * cantidad) };
}

/**
 * Aplica un descuento a la línea. `{ pct }` o `{ precioFinal }` (unitario).
 * Siempre parte del precio de lista: un segundo descuento reemplaza al primero.
 */
export function aplicarDescuentoItem(item, { pct, precioFinal } = {}, extra = {}) {
  if (!item) return item;
  const lista = precioListaItem(item);
  const cantidad = Number(item.cantidad) || 0;

  let unit;
  let pctFinal;
  if (precioFinal !== undefined && precioFinal !== null && precioFinal !== '') {
    unit = round2(clamp(Number(precioFinal) || 0, 0, lista));
    pctFinal = lista > 0 ? round4((1 - unit / lista) * 100) : 0;
  } else {
    pctFinal = round4(clamp(Number(pct) || 0, 0, 100));
    unit = round2(lista * (1 - pctFinal / 100));
  }

  if (!(pctFinal > 0) || unit >= lista) return quitarDescuentoItem(item);

  const base = quitarDescuentoItem(item);
  return {
    ...base,
    ...extra,
    list_unit_price: lista,
    item_discount_pct: pctFinal,
    unit_price: unit,
    subtotal: round2(unit * cantidad),
  };
}

/** Nueva cantidad: el % se mantiene y el subtotal se recalcula sobre el neto. */
export function cambiarCantidadItem(item, cantidad) {
  if (!item) return item;
  const qty = Number(cantidad) || 0;
  return { ...item, cantidad: qty, subtotal: round2((Number(item.unit_price) || 0) * qty) };
}

/**
 * Cambió el precio de lista (lista de precios, catálogo refrescado): si la
 * línea tiene descuento, se mantiene el % sobre el precio nuevo.
 */
export function actualizarPrecioListaItem(item, nuevoPrecioLista) {
  if (!item) return item;
  const lista = Number(nuevoPrecioLista) || 0;
  const cantidad = Number(item.cantidad) || 0;
  if (!tieneDescuentoItem(item)) {
    return { ...item, unit_price: lista, subtotal: round2(lista * cantidad) };
  }
  const unit = round2(lista * (1 - Number(item.item_discount_pct) / 100));
  return { ...item, list_unit_price: lista, unit_price: unit, subtotal: round2(unit * cantidad) };
}

/** Monto total descontado en la línea (lista × cantidad − subtotal neto). */
export function montoDescuentoItem(item) {
  if (!tieneDescuentoItem(item)) return 0;
  const cantidad = Number(item.cantidad ?? item.quantity) || 0;
  return round2(round2(precioListaItem(item) * cantidad) - round2(item.subtotal));
}

/**
 * Decide si un descuento puede aplicarse y qué pide antes.
 * Devuelve { ok, error, requiereAutorizacion, requiereMotivo }.
 */
export function evaluarDescuentoItem({
  pct,
  config,
  puedeAutorizar = false,
  bloqueado = false,
  allowZeroPrice = false,
  precioLista = null,
} = {}) {
  const cfg = config || { enabled: false, maxPct: null, motivoDesdePct: null };
  const p = Number(pct) || 0;
  const fail = (error) => ({ ok: false, error, requiereAutorizacion: false, requiereMotivo: false });

  if (!cfg.enabled) return fail('El descuento por producto no está habilitado en Empresa → Datos.');
  if (bloqueado && !puedeAutorizar) return fail('Este producto está marcado sin descuentos.');
  // Se mira el unitario YA redondeado: un 99,9999% o un precio final de $0,001
  // también dejan la línea en $0.
  const lista = Number(precioLista);
  const quedaEnCero = p >= 100 || (Number.isFinite(lista) && lista > 0 && round2(lista * (1 - p / 100)) <= 0);
  if (quedaEnCero && !allowZeroPrice) return fail('El precio no puede quedar en $0: la empresa no permite precio de venta cero.');

  return {
    ok: true,
    error: null,
    requiereAutorizacion: cfg.maxPct !== null && p > cfg.maxPct && !puedeAutorizar,
    requiereMotivo: cfg.motivoDesdePct !== null && p >= cfg.motivoDesdePct,
  };
}

/**
 * Revalidación al confirmar la venta, contra la config leída fresca de la base.
 * Devuelve la lista de problemas (vacía = ok).
 */
export function validarDescuentosCarrito(carrito = [], config, { puedeAutorizar = false } = {}) {
  const cfg = config || { enabled: false, maxPct: null, motivoDesdePct: null };
  const problemas = [];
  for (const item of Array.isArray(carrito) ? carrito : []) {
    if (!tieneDescuentoItem(item)) continue;
    const pct = Number(item.item_discount_pct);
    if (!cfg.enabled) {
      problemas.push({ tipo: 'desactivado', cart_key: item.cart_key });
      continue;
    }
    if (cfg.maxPct !== null && pct > cfg.maxPct && !puedeAutorizar && !item.item_discount_by) {
      problemas.push({ tipo: 'supera_tope', cart_key: item.cart_key });
      continue;
    }
    if (cfg.motivoDesdePct !== null && pct >= cfg.motivoDesdePct && !String(item.item_discount_reason || '').trim()) {
      problemas.push({ tipo: 'falta_motivo', cart_key: item.cart_key });
    }
  }
  return problemas;
}

/** Columnas de sale_items para la línea; {} si no tiene descuento propio. */
export function camposDescuentoSaleItem(item, { userId = null } = {}) {
  if (!tieneDescuentoItem(item)) return {};
  return {
    list_unit_price: round2(precioListaItem(item)),
    item_discount_pct: round4(item.item_discount_pct),
    item_discount_amount: montoDescuentoItem(item),
    item_discount_by: item.item_discount_by || userId || null,
    item_discount_reason: String(item.item_discount_reason || '').trim() || null,
  };
}

/**
 * Permiso de autorizar descuentos por encima del tope (y sobre productos sin
 * descuentos). Admin/administrador siempre; el resto necesita el módulo
 * `ventas-pos-descuento-autorizar` (o 'all'). Falla cerrado sin perfil.
 */
export function canAutorizarDescuentoItem({ profile = null, isSuperAdmin = false, effectiveModules = null } = {}) {
  if (isSuperAdmin) return true;
  if (!profile) return false;
  const role = String(profile.role || '').trim().toLowerCase();
  if (PRIVILEGED_ROLES.has(role)) return true;
  const modules = effectiveModules instanceof Set
    ? effectiveModules
    : new Set(Array.isArray(effectiveModules) ? effectiveModules : []);
  return modules.has('all') || modules.has(ITEM_DISCOUNT_AUTORIZAR_MODULE_ID);
}
